<template>
  <v-card class="elevation-0">
    <v-progress-linear v-if="isLoading" indeterminate color="primary"></v-progress-linear>
    <v-form v-model="valid">
      <v-card-text :style="isLoading ? 'opacity: 0.6' : ''">
        <test-step-table-two :packId="packId"></test-step-table-two>
        <v-row wrap>
          <v-col sm="12" md="3">
            <v-text-field
              id="e2e-testStepNumber"
              v-model="testStepRecord.stepNumber"
              label="Step Number *"
              :rules="stepNumberRules"
              required
              type="number"
              :disabled="isLoading"
              data-cy="testStepForm__input--stepNumber"
            ></v-text-field>
          </v-col>
          <v-col sm="12" md="3">
            <v-text-field
              id="e2e-testSubstepNumber"
              v-model="testStepRecord.substepNumber"
              label="Substep Number"
              type="number"
              :disabled="isLoading"
              data-cy="testStepForm__input--substepNumber"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col lg="8">
            <div id="e2e-testStepScenario">
              <label>
                <strong>Scenario</strong>
              </label>
              <ckeditor
                v-if="!isLoading"
                :config="ckeditorConfig"
                :key="scenarioKey"
                @focus="setCurrentEditor($event)"
                id="scenario"
                ref="scenario"
                v-model="testStepRecord.scenario"
              ></ckeditor>
              <v-skeleton-loader v-else type="article" class="mt-2"></v-skeleton-loader>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col lg="8">
            <div>
              <label>
                <strong>Expected Outcome</strong>
              </label>
              <ckeditor
                v-if="!isLoading"
                :config="ckeditorConfig"
                :key="expectedOutcomeKey"
                @focus="setCurrentEditor($event)"
                data-cy="testStepForm__editor--expectedOutcome"
                id="expectedOutcome"
                ref="expectedOutcome"
                v-model="testStepRecord.expectedOutcome"
              ></ckeditor>
              <v-skeleton-loader v-else type="article" class="mt-2"></v-skeleton-loader>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="cancel" :disabled="isLoading" data-cy="testStepForm__button--cancel" outlined>Cancel</v-btn>
        <v-btn @click="save" :disabled="!valid || isLoading" color="primary" data-cy="testStepForm__button--save"
          >Save Test Step</v-btn
        >
        <v-btn
          data-cy="testStepForm__button--saveAndCreate"
          @click="saveAndCreate"
          :disabled="!valid || isLoading"
          color="primary"
          >Save Test Step and Create Another</v-btn
        >
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import Ckeditor from "vue-ckeditor2";

import TestStepTableTwo from "@/components/testStepTableTwo";
import { mapActions } from "vuex";

export default {
  name: "testStepForm",
  components: {
    Ckeditor,
    TestStepTableTwo,
  },
  computed: {
    packId() {
      return parseInt(this.$route.params.packId, 10);
    },
    releaseNote() {
      return this.$store.state.releaseNote.releaseNoteRecord;
    },
    releaseNoteId() {
      return parseInt(this.$route.params.releaseNoteId, 10);
    },
    testStepRecord() {
      return this.$store.state.testStep.testStepRecord;
    },
    testStepId() {
      return parseInt(this.$route.params.testStepId, 10);
    },
    testStepList() {
      return this.$store.getters["testStep/testStepList"](this.releaseNoteId);
    },
  },
  async created() {
    this.isLoading = true;

    // Always reset test step record first to clear any stale data from previous navigation
    if (this.mode === "create") {
      this.resetTestStepRecord();
      await this.$nextTick();
      this.forceRerenderToClearContent();
    }

    try {
      await this.fetchData();
    } catch (e) {
      console.error(e);
      this.$notifyError("Error fetching data");
    } finally {
      this.isLoading = false;
    }
  },
  data() {
    return {
      ckeditorConfig: {
        toolbarGroups: [
          { name: "basicstyles", groups: ["basicstyles", "cleanup"] },
          { name: "paragraph", groups: ["list", "indent", "blocks", "align", "bidi", "paragraph"] },
          { name: "links", groups: ["links"] },
          { name: "insert", groups: ["insert"] },
          "/",
          { name: "styles", groups: ["styles"] },
          { name: "colors", groups: ["colors"] },
          { name: "tools", groups: ["tools"] },
          { name: "others", groups: ["others"] },
          { name: "about", groups: ["about"] },
          { name: "editing", groups: ["find", "selection", "editing"] },
          { name: "forms", groups: ["forms"] },
          { name: "document", groups: ["mode", "document", "doctools"] },
          { name: "clipboard", groups: ["clipboard", "undo"] },
        ],
        removeButtons:
          "Flash,Iframe,PageBreak,Language,About,Subscript,Superscript,CopyFormatting,Anchor,Find,Replace,SelectAll,Cut,Copy,PasteFromWord,Paste,Templates,Save,NewPage,Preview,Print,Form,Checkbox,Radio,TextField,Textarea,Select,Button,ImageButton,HiddenField",
        height: 200,
        versionCheck: false,
      },
      expectedOutcomeKey: 0,
      isLoading: false,
      scenarioKey: 0,
      currentEditor: null,
      stepNumberRules: [
        stepNumber => !!stepNumber || "Step Number is required",
        stepNumber => stepNumber > 0 || "Step Number must be greater than 0",
      ],

      valid: true,
      versionCheck: false,
    };
  },
  methods: {
    cancel() {
      this.$router.push({
        name: "releaseNoteView",
        params: {
          releaseNoteId: this.releaseNoteId,
        },
        query: {
          tab: "test-steps",
        },
      });
      this.resetTestStepRecord();
    },
    isValidId(id) {
      return typeof id === "number" && Number.isInteger(id) && id > 0;
    },
    async fetchData() {
      await Promise.all([
        this.getReleaseNote({
          id: this.releaseNoteId,
        }),
        this.fetchTestStepList({
          releaseNoteId: this.releaseNoteId,
        }),
      ]);

      if (this.mode === "edit" && this.testStepId) {
        await this.fetchTestStep({
          testStepId: this.testStepId,
        });
      } else {
        this.resetTestStepRecord();
      }
    },
    forceRerenderToClearContent() {
      // The iframes require a forced rerendering to clear the content.
      // This method uses the key changing technique: https://michaelnthiessen.com/force-re-render/
      this.expectedOutcomeKey += 1;
      this.scenarioKey += 1;
    },
    async save() {
      try {
        if (this.mode === "edit") {
          await this.updateTestStep({ data: this.testStepRecord, releaseNoteId: this.releaseNoteId });
          this.$notifySuccess("Successfully saved Test Step details.");
        } else {
          await this.createTestStep({
            releaseNoteId: this.releaseNoteId,
          });
          this.$notifySuccess("Successfully saved Test Step details.");
        }

        this.$router.push({
          name: "releaseNoteView",
          params: {
            releaseNoteId: this.releaseNoteId,
          },
          query: {
            tab: "test-steps",
          },
        });
      } catch (e) {
        console.error("Test Step record did not save", e);
        this.$notifyError("There was an error saving Test Step details.");
      }
    },
    async saveAndCreate() {
      try {
        if (this.mode === "edit") {
          await this.updateTestStep({ data: this.testStepRecord, releaseNoteId: this.releaseNoteId });
          this.$notifySuccess("Successfully saved Test Step details.");
        } else {
          await this.createTestStep({
            releaseNoteId: this.releaseNoteId,
          });
          this.$notifySuccess("Successfully saved Test Step details.");
        }

        // only push to create view if not already on create view
        if (this.$route.name !== "testStepCreate") {
          this.$router.push({
            name: "testStepCreate",
          });
        }

        this.$nextTick(() => {
          this.forceRerenderToClearContent();
          this.resetTestStepRecord();
        });
      } catch (e) {
        console.error("Test Step record did not save", e);
        this.$notifyError("There was an error saving Test Step details.");
      }
    },
    registerEventListener(editor) {
      const editableContent = editor.editable();
      editableContent.attachListener(editableContent, "blur", e => {
        if (editableContent.getData() !== this[editor.name]) {
          this[editor.name] = editableContent.getData();
        }
      });
    },
    setCurrentEditor({ editor }) {
      this.currentEditor = editor.name;
      this.registerEventListener(editor);
    },
    ...mapActions({
      createTestStep: "testStep/createTestStep",
      fetchTestStep: "testStep/getTestStep",
      fetchTestStepList: "testStep/list",
      getReleaseNote: "releaseNote/getReleaseNote",
      resetTestStepRecord: "testStep/resetTestStepRecord",
      updateTestStep: "testStep/updateTestStep",
    }),
  },
  props: {
    mode: {
      type: String,
      default: "create",
    },
  },
  watch: {
    async releaseNoteId(newVal, oldVal) {
      if (this.isValidId(newVal) && this.isValidId(oldVal) && newVal !== oldVal) {
        this.isLoading = true;

        // Reset test step record before fetching new data to prevent stale content
        if (this.mode === "create") {
          this.resetTestStepRecord();
          await this.$nextTick();
          this.forceRerenderToClearContent();
        }

        try {
          await this.fetchData();
        } catch (e) {
          console.error(e);
          this.$notifyError("Error fetching data");
        } finally {
          this.isLoading = false;
        }
      }
    },
    async testStepId(newVal, oldVal) {
      if (this.isValidId(newVal) && this.isValidId(oldVal) && newVal !== oldVal) {
        this.isLoading = true;
        try {
          await this.fetchData();
        } catch (e) {
          console.error(e);
          this.$notifyError("Error fetching data");
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
};
</script>
